import React, { useRef, useState } from 'react';

import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { Label } from '@buildoutinc/blueprint-react/ui/Label';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip, faPaperPlaneTop, faXmark } from '@fortawesome/pro-regular-svg-icons';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import LoadingSpinner from 'components/ui/loading_spinner';
import { formatBytes } from 'helpers/formatters/file_size_formats';

import EmailRecipientInput from './email_recipient_input';
import EmailTextEditor from './email_text_editor';
import { getFileName, getFileSize, MAX_ATTACHMENT_BYTES } from '../send_email/helpers';

import type { Recipient } from './email_recipient';
import type { EmailFile } from '../send_email/types';

export interface EmailFormValues {
  bcc: string;
  body: string;
  cc: string;
  subject: string;
  to: Recipient[];
}

interface Props {
  fromEmail?: string;
  value: EmailFormValues;
  files: EmailFile[];
  sending: boolean;
  onChange: (changes: Partial<EmailFormValues>) => void;
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (index: number) => void;
  onSend: () => void;
}

const LABEL_CLASS = 'text-muted mb-0 flex-shrink-0';
const LABEL_WIDTH = { minWidth: '4.25rem' };

export default function EmailPanel({
  fromEmail,
  value,
  files,
  sending,
  onChange,
  onAddFiles,
  onRemoveFile,
  onSend,
}: Props) {
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [editingSubject, setEditingSubject] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const tooLarge = files.reduce((sum, file) => sum + getFileSize(file), 0) > MAX_ATTACHMENT_BYTES;
  const canSend = value.to.length > 0 && !tooLarge && !sending;

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      onAddFiles(Array.from(e.target.files));
    }
    e.target.value = '';
  };

  return (
    <div className="d-flex flex-column gap-3">
      <div>
        <div className="d-flex align-items-center gap-2 py-2 border-bottom">
          <Label className={LABEL_CLASS} htmlFor="logActivityEmailTo" style={LABEL_WIDTH}>To:</Label>
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <EmailRecipientInput
              id="logActivityEmailTo"
              recipients={value.to}
              onChange={to => onChange({ to })}
            />
          </div>
          <div className="d-flex gap-2 flex-shrink-0">
            {!showCc && <Button label="Cc" size="sm" variant="ghost" onClick={() => setShowCc(true)} />}
            {!showBcc && <Button label="Bcc" size="sm" variant="ghost" onClick={() => setShowBcc(true)} />}
          </div>
        </div>

        {showCc && (
          <div className="d-flex align-items-center gap-2 py-2 border-bottom">
            <Label className={LABEL_CLASS} htmlFor="logActivityEmailCc" style={LABEL_WIDTH}>Cc:</Label>
            <Input
              className="border-0 shadow-none p-0 flex-grow-1"
              id="logActivityEmailCc"
              value={value.cc}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ cc: e.target.value })}
            />
          </div>
        )}

        {showBcc && (
          <div className="d-flex align-items-center gap-2 py-2 border-bottom">
            <Label className={LABEL_CLASS} htmlFor="logActivityEmailBcc" style={LABEL_WIDTH}>Bcc:</Label>
            <Input
              className="border-0 shadow-none p-0 flex-grow-1"
              id="logActivityEmailBcc"
              value={value.bcc}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ bcc: e.target.value })}
            />
          </div>
        )}

        <div className="d-flex align-items-center gap-2 py-2 border-bottom">
          <Label className={LABEL_CLASS} style={LABEL_WIDTH}>From:</Label>
          <span className="fw-medium">{fromEmail || 'Your connected email address'}</span>
        </div>

        <div className="d-flex align-items-center gap-2 py-2 border-bottom">
          <Label className={LABEL_CLASS} htmlFor="logActivityEmailSubject" style={LABEL_WIDTH}>Subject:</Label>
          {editingSubject ? (
            <Input
              autoFocus
              className="border-0 shadow-none p-0 flex-grow-1"
              id="logActivityEmailSubject"
              value={value.subject}
              onBlur={() => setEditingSubject(false)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ subject: e.target.value })}
            />
          ) : (
            <Button
              className="p-0 flex-grow-1 justify-content-start text-start fw-normal"
              variant="ghost"
              onClick={() => setEditingSubject(true)}
            >
              <span className={value.subject ? '' : 'text-muted'}>
                {value.subject || 'Enter subject here...'}
              </span>
            </Button>
          )}
        </div>
      </div>

      <EmailTextEditor value={value.body} onChange={body => onChange({ body })} />

      {tooLarge && <Alert severity="warning">Attachments exceed the 25 MB limit.</Alert>}

      {files.length > 0 && (
        <div className="d-flex flex-column gap-2">
          {files.map((file, index) => (
            <div
              className="d-flex align-items-center justify-content-between border rounded px-3 py-2"
              key={`${getFileName(file)}-${index}`}
            >
              <span className="text-truncate me-2">
                <FontAwesomeIcon className="me-2 text-muted" icon={faPaperclip} />
                {getFileName(file)}
                <span className="text-muted fs-xs ms-2">{formatBytes(getFileSize(file))}</span>
              </span>
              <Button
                aria-label={`Remove ${getFileName(file)}`}
                icon={faXmark}
                size="sm"
                variant="ghost"
                onClick={() => onRemoveFile(index)}
              />
            </div>
          ))}
        </div>
      )}

      <input className="d-none" multiple ref={fileInput} type="file" onChange={handleFileInput} />

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <Button
          icon={faPaperclip}
          label="Attachments"
          size="sm"
          variant="ghost"
          onClick={() => fileInput.current?.click()}
        />
        <Button
          disabled={!canSend}
          iconEnd={faPaperPlaneTop}
          onClick={onSend}
        >
          {sending && <LoadingSpinner flex size="md" skipTopBuffer />}
          {sending ? 'Sending…' : 'Send Email'}
        </Button>
      </div>
    </div>
  );
}
